import { z } from 'zod';
import { FLIGHT_TYPE_KEYS, MOTOR_CHAIN_TYPE_KEYS } from './motor-calc';

/**
 * Motorberechnung und Kontrolle Hauptwelle je Projekt (VB-26025) — Schemas für
 * die Endpunkte.
 *
 * Erfasst wird nur, was es nirgends sonst gibt. Beckenmaße kommen vom Projekt,
 * Balkenanzahl/-länge aus der Projektauslegung (ADR 0094) und die Kettenlänge aus
 * der Kettenberechnung; sie stehen in der Antwort mit dabei, sind aber **nicht**
 * eingebbar — sonst gäbe es zwei Wahrheiten (§6).
 */

export const flightTypeSchema = z.enum(FLIGHT_TYPE_KEYS as [string, ...string[]]);
export const motorChainTypeSchema = z.enum(MOTOR_CHAIN_TYPE_KEYS as [string, ...string[]]);

export const saveMotorCalcSchema = z.object({
  /** Teilkreisdurchmesser des Förderkettenrads in m. */
  sprocketDiameterM: z.number().positive('Der Durchmesser muss größer als 0 sein.').max(10),
  /** Derselbe Teilkreis in cm — die Drehzahltabelle des Blattes rechnet in cm. */
  sprocketPitchCm: z.number().positive('Der Teilkreis muss größer als 0 sein.').max(1000),
  pinionTeeth: z.number().int().min(1).max(200),
  sprocketTeeth: z.number().int().min(1).max(200),
  /** Gewünschte Räumergeschwindigkeit in cm/s. */
  scraperSpeedCmS: z.number().positive('Die Geschwindigkeit muss größer als 0 sein.').max(100),
  /** Reibungskoeffizient (Robalon–Niro: 0,25). */
  frictionCoefficient: z.number().positive('Der Reibwert muss größer als 0 sein.').max(5),
  flightType: flightTypeSchema,
  chainType: motorChainTypeSchema,
  note: z.string().trim().max(2000).nullish(),
});
export type SaveMotorCalcInput = z.infer<typeof saveMotorCalcSchema>;

const gearboxOptionSchema = z.object({
  kind: z.enum(['SPUR', 'WORM', 'BEVEL']),
  maxAreaM2: z.number().nullable(),
  maxTorqueNm: z.number(),
  gearbox: z.string(),
  motor: z.string(),
  power: z.string(),
  speedRpm: z.number(),
  poles: z.number().int(),
  isTorqueSufficient: z.boolean(),
});

export const motorCalcResponseSchema = z.object({
  projectId: z.string().uuid(),
  input: saveMotorCalcSchema,
  /**
   * Was aus Projekt, Auslegung und Kettenberechnung kommt. `null` heißt: dort
   * steht noch nichts — die Oberfläche sagt dann, wo der Wert hingehört.
   */
  fromProject: z.object({
    basinLengthM: z.number().nullable(),
    basinWidthM: z.number().nullable(),
    flightLengthM: z.number().nullable(),
    flightCount: z.number().nullable(),
    chainLengthM: z.number().nullable(),
    chainLinkLengthMm: z.number().nullable(),
  }),
  /** Gerechnetes Ergebnis; null, solange Eingaben fehlen. */
  result: z
    .object({
      flightWeightKg: z.number(),
      chainWeightKg: z.number(),
      normalForceN: z.number(),
      frictionPerFlightN: z.number(),
      frictionTotalN: z.number(),
      torqueNm: z.number(),
      basinAreaM2: z.number(),
      outputSpeedRpm: z.number(),
      gearboxes: z.object({
        SPUR: gearboxOptionSchema.nullable(),
        WORM: gearboxOptionSchema.nullable(),
        BEVEL: gearboxOptionSchema.nullable(),
      }),
      issues: z.array(z.string()),
    })
    .nullable(),
  /** Was in Projekt oder Auslegung fehlt — in der Sprache des Datenblatts. */
  missing: z.array(z.string()),
  updatedAt: z.string().datetime().nullable(),
});
export type MotorCalcResponse = z.infer<typeof motorCalcResponseSchema>;

export const saveShaftCheckSchema = z.object({
  wallDistanceMm: z.number().min(0).max(100_000),
  x1Mm: z.number().min(0).max(100_000),
  x2Mm: z.number().min(0).max(100_000),
  x3Mm: z.number().min(0).max(100_000),
  x4Mm: z.number().min(0).max(100_000),
  x5Mm: z.number().min(0).max(100_000),
  x6Mm: z.number().min(0).max(100_000),
  note: z.string().trim().max(2000).nullish(),
});
export type SaveShaftCheckInput = z.infer<typeof saveShaftCheckSchema>;

export const shaftCheckResponseSchema = z.object({
  projectId: z.string().uuid(),
  input: saveShaftCheckSchema,
  /** Beckenbreite vom Projekt — treibt den Wellenvorschlag. */
  fromProject: z.object({ basinWidthM: z.number().nullable() }),
  result: z.object({
    leftMm: z.number(),
    rightMm: z.number(),
    leftDeviationMm: z.number(),
    rightDeviationMm: z.number(),
    shaftDiameter: z.string().nullable(),
    issues: z.array(z.string()),
  }),
  /** Sollmaß, damit die Oberfläche es nicht zweitmalig festschreibt. */
  targetDistanceMm: z.number(),
  updatedAt: z.string().datetime().nullable(),
});
export type ShaftCheckResponse = z.infer<typeof shaftCheckResponseSchema>;
